import React from "react";
import FontIcon from "react-md/lib/FontIcons";
import Tooltip from "@material-ui/core/Tooltip";
import moment from "moment";
import {
  ROLE_DOCTOR,
  ROLE_COORDINATOR,
  CALL_ANSWERED,
  CALL_MISSED,
  CALL_ONGOING,
  CALL_OUTCOME_LABEL_MAP,
} from "../../constants";
import AppointmentLink from "./AppointmentLink";

export const ROLE_DESCRIPTION = {
  [ROLE_DOCTOR]: "Doctor · clinical protocol",
  [ROLE_COORDINATOR]: "Coordinator · follow-ups & adherence",
};

const ROLE_SHORT = { [ROLE_DOCTOR]: "Doctor", [ROLE_COORDINATOR]: "Coordinator" };

export function talkTime(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m === 0) return `${rem}s`;
  return `${m}m ${String(rem).padStart(2, "0")}s`;
}

export const nameOf = (names, id) => (id && names && names[String(id)]) || (id ? `Employee ${id}` : "-");

function initials(name) {
  return (
    (name || "?")
      .replace(/^dr\.?\s+/i, "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0].toUpperCase())
      .join("") || "?"
  );
}

function DateTimeStack({ value }) {
  if (!value) return <span>-</span>;
  const m = moment(value);
  return (
    <span className="ltr-datetime">
      <span className="ltr-datetime-date">{m.format("D MMM YYYY")}</span>
      <span className="ltr-datetime-time">{m.format("h:mm A")}</span>
    </span>
  );
}

/**
 * Call audit: every outreach call on the case with its outcome, talk time and
 * note, and the per-caller and whole-case tallies above it. An answered (or
 * still-live) call that has not become an appointment offers the convert
 * button in its Appointment ID cell.
 */
function CallAudit({ caseRecord, names, isClosed, onAssignTask, onConvert, onRemark }) {
  const calls = Array.isArray(caseRecord.calls) ? caseRecord.calls : [];
  const totalAttempts = Math.max(caseRecord.callCount || 0, calls.length);
  const answered = calls.filter((c) => c.outcome === CALL_ANSWERED);
  const missed = calls.filter((c) => c.outcome === CALL_MISSED);
  const totalTalk = answered.reduce((sum, c) => sum + (c.durationSeconds || 0), 0);

  const byCaller = [];
  calls.forEach((c) => {
    let entry = byCaller.find((e) => e.id === c.callerId);
    if (!entry) {
      entry = { id: c.callerId, role: c.callerRole, calls: 0, answered: 0, missed: 0, talk: 0 };
      byCaller.push(entry);
    }
    entry.calls += 1;
    if (c.outcome === CALL_ANSWERED) {
      entry.answered += 1;
      entry.talk += c.durationSeconds || 0;
    }
    if (c.outcome === CALL_MISSED) entry.missed += 1;
  });

  return (
    <section className="ltr-detail-card">
      <div className="ltr-card-head">
        <div>
          <h3>Call audit</h3>
          <p className="ltr-card-sub">Every outreach call on this case, with its note and recording.</p>
        </div>
        {!isClosed && (
          <button type="button" className="ltr-btn" onClick={onAssignTask}>
            + Assign task
          </button>
        )}
      </div>

      <div className="ltr-stat-row">
        <div className="ltr-stat">
          <span className="ltr-stat-label">Total attempts</span>
          <span className="ltr-stat-value">{totalAttempts}</span>
        </div>
        <div className="ltr-stat">
          <span className="ltr-stat-label">Answered</span>
          <span className="ltr-stat-value answered">{answered.length}</span>
        </div>
        <div className="ltr-stat">
          <span className="ltr-stat-label">Missed</span>
          <span className="ltr-stat-value missed">{missed.length}</span>
        </div>
        <div className="ltr-stat">
          <span className="ltr-stat-label">Total talk time</span>
          <span className="ltr-stat-value">{talkTime(totalTalk)}</span>
        </div>
      </div>

      {byCaller.length > 0 && (
        <div className="ltr-caller-row">
          {byCaller.map((p) => {
            const name = nameOf(names, p.id);
            const parts = [`${p.calls} call${p.calls === 1 ? "" : "s"}`, `${p.answered} answered`];
            if (p.missed) parts.push(`${p.missed} missed`);
            if (p.talk) parts.push(talkTime(p.talk));
            return (
              <div key={p.id} className="ltr-caller-card">
                <span className="ltr-avatar">{initials(name)}</span>
                <div>
                  <div className="ltr-caller-name">{name}</div>
                  {p.role && <div className="ltr-caller-role">{ROLE_DESCRIPTION[p.role] || p.role}</div>}
                  <div className="ltr-caller-meta">{parts.join(" · ")}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="ltr-table-wrap">
        <table className="ltr-table">
          <thead>
            <tr>
              <th>Call ID</th>
              <th>Caller</th>
              <th>When</th>
              <th>Outcome</th>
              <th>Duration</th>
              <th>Remark</th>
              <th>Recording</th>
              <th>Appointment ID</th>
            </tr>
          </thead>
          <tbody>
            {calls.length === 0 ? (
              <tr>
                <td colSpan={8} className="ltr-table-empty">
                  No calls logged yet. Use Call patient to reach the patient.
                </td>
              </tr>
            ) : (
              calls.map((c) => {
                const convertible =
                  !isClosed && !c.appointmentId && (c.outcome === CALL_ANSWERED || c.outcome === CALL_ONGOING);
                return (
                  <tr key={c.callId}>
                    <td className="ltr-strong">{c.callId}</td>
                    <td>
                      <div className="ltr-strong">{nameOf(names, c.callerId)}</div>
                      {c.callerRole && <div className="ltr-cell-sub">{ROLE_SHORT[c.callerRole] || c.callerRole}</div>}
                    </td>
                    <td>
                      <DateTimeStack value={c.startedAt} />
                    </td>
                    <td>
                      <span className={`ltr-outcome-pill ${c.outcome}`}>
                        {CALL_OUTCOME_LABEL_MAP[c.outcome] || c.outcome}
                      </span>
                    </td>
                    <td>{c.outcome === CALL_ANSWERED ? talkTime(c.durationSeconds) : "-"}</td>
                    <td className="ltr-remark-cell">
                      {c.remark ? (
                        <button type="button" className="ltr-remark-text" onClick={() => onRemark(c)}>
                          {c.remark}
                        </button>
                      ) : (
                        <button type="button" className="ltr-text-btn" onClick={() => onRemark(c)}>
                          Add remark
                        </button>
                      )}
                    </td>
                    <td>
                      <Tooltip arrow placement="top" title="Recordings are not available in Bridge yet">
                        <span className="ltr-muted">—</span>
                      </Tooltip>
                    </td>
                    <td>
                      {c.appointmentId ? (
                        <AppointmentLink appointmentId={c.appointmentId} />
                      ) : convertible ? (
                        <Tooltip arrow placement="top" title="Convert this call into an appointment">
                          <button
                            type="button"
                            className="ltr-icon-chip"
                            onClick={() => onConvert(c)}
                            aria-label="Convert this call into an appointment"
                          >
                            <FontIcon>event</FontIcon>
                          </button>
                        </Tooltip>
                      ) : (
                        <span className="ltr-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default CallAudit;
